import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useActorRef } from '@xstate/react';
import { format } from 'date-fns';
import { fromPromise } from 'xstate';

import { useNoticeBannersStackActions } from '@sg/garnish';

import { useAppsFlyer } from '@order/integrations-appsflyer';
import { useCreateRadarSession } from '@order/integrations-stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';
import { getFetchOptions } from '@order/utils';

import {
  checkIfAccessTokenHasExpired,
  refreshAccessTokens,
} from '../../../../../context/AzureAuth';
import { useCreateAccountMutation } from '../../../graphql/CreateAccount.generated';
import { useSignInMutation } from '../../../graphql/SignIn.generated';
import { joinOrSignInStateMachine } from './join-or-sign-in-machine';
import {
  type CreateAccountActorInput,
  type CreateAccountOutput,
  type MessageParams,
  type RefreshAccessTokensInput,
  type RefreshAccessTokensOutput,
  type SignInInput,
  type SignInOutput,
} from './join-or-sign-in-machine.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useJoinOrSignInService = () => {
  const { formatMessage } = useIntl();
  const { push: addBanner } = useNoticeBannersStackActions();

  const { track } = useTelemetry();
  const { logCompleteRegistrationEvent } = useAppsFlyer();

  const isRadarRegistrationEvaluationEnabled = useFeatureFlag(
    'CELS-5659-radar-registration-evaluation-enabled',
  );

  // ─── Radar ───────────────────────────────────────────────────────────

  const createRadarSession = useCreateRadarSession();

  // ─── Network Requests ────────────────────────────────────────────────

  const [, executeSignIn] = useSignInMutation();
  const [, executeCreateAccount] = useCreateAccountMutation();

  // ─── Actions ─────────────────────────────────────────────────────────

  const onErrorMessage = useCallback(
    (_args: unknown, params: MessageParams) => {
      if (!params.text) return;

      addBanner({ palette: 'caution', text: params.text });
    },
    [addBanner],
  );

  const onSuccessMessage = useCallback(
    (_args: unknown, params: MessageParams) => {
      if (!params.text) return;

      addBanner({ palette: 'success', text: params.text });
    },
    [addBanner],
  );

  // ─── Services ────────────────────────────────────────────────────────

  const signIn = useMemo(
    () =>
      fromPromise<SignInOutput, SignInInput>(async ({ input }) => {
        const { accessToken } = input;

        if (!accessToken) {
          throw new Error('Sign in failed | No access tokens');
        }

        const response = await executeSignIn(
          {},
          {
            fetchOptions: getFetchOptions({
              extraHeaders: {
                [API_ACCESS_TOKEN_HEADER]: accessToken,
              },
            }),
          },
        );
        const { data, error } = response;
        const responseType = data?.signIn.__typename;

        const hasUserError = responseType === 'ValidationError' || responseType === 'SignInFailed';
        const systemError = hasUserError ? data?.signIn.message : error?.message;

        if (responseType === 'SignInSuccess') {
          return { hasSignedIn: true };
        }

        if (responseType === 'CustomerNotFound') {
          return { hasSignedIn: false };
        }

        throw new Error(systemError);
      }),
    [executeSignIn],
  );

  const createAccount = useMemo(
    () =>
      fromPromise<CreateAccountOutput, CreateAccountActorInput>(async ({ input: actorInput }) => {
        const { email, accessToken: token, createAccountInput } = actorInput;
        const input = createAccountInput;

        if (!token || !input) {
          return { hasCreatedAccount: false };
        }

        const radarSession = isRadarRegistrationEvaluationEnabled
          ? await createRadarSession()
          : undefined;

        const response = await executeCreateAccount(
          { input: { ...input, radarSession } },
          {
            fetchOptions: getFetchOptions({
              extraHeaders: {
                [API_ACCESS_TOKEN_HEADER]: token,
              },
            }),
          },
        );

        const data = response.data?.createAccount;
        const hasCreatedAccount = data?.__typename === 'CreateAccountSuccess';

        // ─── Error Handling ──────────────────────────────────────────────────

        if (!hasCreatedAccount || response.error) {
          const hasValidationErrors = data?.__typename === 'ValidationError';

          const validationError = hasValidationErrors ? data.fieldErrors?.[0]?.message : undefined;
          const userError = validationError ?? formatMessage(messages.joinError);
          const systemError = response.error?.message;

          addBanner({ text: userError, palette: 'caution' });

          track('join.failure', { userError, systemError });

          return { hasCreatedAccount };
        }

        // ─────────────────────────────────────────────────────────────────────

        track('join.success', {
          name: `${input.firstName} ${input.lastName}`,
          email,
          date: format(new Date(), 'M/dd/yyyy K:mmbbb'),
          referred: false,
        });

        logCompleteRegistrationEvent();

        return { hasCreatedAccount };
      }),
    [
      addBanner,
      createRadarSession,
      executeCreateAccount,
      formatMessage,
      isRadarRegistrationEvaluationEnabled,
      logCompleteRegistrationEvent,
      track,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  const refreshAccessTokensActor = useMemo(
    () =>
      fromPromise<RefreshAccessTokensOutput, RefreshAccessTokensInput>(async ({ input }) =>
        refreshAccessTokens(input.refreshToken),
      ),
    [],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useActorRef(
    joinOrSignInStateMachine.provide({
      actions: {
        //
        // ─── Debug Message Loggers ───────────────────────────

        onDebugMessage(_args, params) {
          track('join-or-sign-in.embedded-frame.debug', {
            data: stringifyPayload(params.data),
          });
        },

        // ─── Error Handlers ──────────────────────────────────

        onInitError(_args, params) {
          track('join-or-sign-in.failure', {
            flow: 'embedded',
            reason: 'Initialization error',
            systemError: stringifyPayload(params.data),
          });
        },
        onInitTimeoutError() {
          track('join-or-sign-in.failure', {
            flow: 'embedded',
            reason: 'Initialization timeout error',
          });
        },
        onVerificationScreenLoadingTimeout() {
          track('join-or-sign-in.failure', {
            flow: 'embedded',
            reason: 'Verification screen loading timeout error',
          });
        },
        onVerificationTimeout() {
          track('join-or-sign-in.failure', {
            flow: 'embedded',
            reason: 'Code verification timeout error',
          });
        },
        onSignInSystemError(_args, params) {
          track('join-or-sign-in.failure', {
            flow: 'embedded',
            reason: 'Sign-in mutation error',
            systemError: stringifyPayload(params.error),
          });
        },
        onRefreshAccessTokensSystemError(_args, params) {
          track('join-or-sign-in.failure', {
            flow: 'embedded',
            reason: 'Refresh access tokens error',
            systemError: stringifyPayload(params.error),
          });
        },
        onCreateAccountSystemError(_args, params) {
          track('join-or-sign-in.failure', {
            flow: 'embedded',
            reason: 'Create account mutation error',
            systemError: stringifyPayload(params.error),
          });
        },

        // ─── Post Message Handlers ───────────────────────────

        onErrorMessage,
        onSuccessMessage,
      },
      actors: {
        createAccount,
        signIn,
        refreshAccessTokens: refreshAccessTokensActor,
      },
      guards: {
        checkIfNeedToRefreshTokens({ context }) {
          const { accessToken } = context;

          if (!accessToken) return true;

          return checkIfAccessTokenHasExpired({
            accessToken,
            safeGuardMinutes: 5,
          });
        },
      },
    }),
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  joinError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Join | Error',
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stringifyPayload(payload: unknown): string {
  try {
    return JSON.stringify(serializeError(payload), null, 2);
  } catch {
    return payload as string;
  }
}

/**
 * `JSON.stringify` drops the non-enumerable `name` and `message` of `Error` instances.
 */
function serializeError(payload: unknown) {
  if (!(payload instanceof Error)) {
    return payload;
  }

  const { name, message, ...fields } = payload;

  return { name, message, ...fields };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const API_ACCESS_TOKEN_HEADER = 'ApiAuthorizationToken';
